import type { CaseAgenticMissionLink } from "@/services/api/case-checking";
import { missionControlTaskDetailPath } from "@/navigation/apps/missionControlLocation";
import { buildMissionDetailWindowUrl } from "@/navigation/apps/missionDetailLocation";

import { isAgenticMissionBlocked } from "./agenticCaseProgress";

export type MissionAttentionKind =
  | "running"
  | "needs_review"
  | "blocked_system"
  | "blocked_client"
  | "chain_failed"
  | "intake_failed";

export type SafeAction = "retry_chain" | "retry_task" | "add_documents" | "retry_intake" | "skip_adjudication";

const SAFE_ACTIONS: SafeAction[] = ["retry_task", "retry_chain", "retry_intake", "add_documents", "skip_adjudication"];

function safeActionsFromLink(link: CaseAgenticMissionLink, fallback: SafeAction[]): SafeAction[] {
  const fromApi = (link.allowed_actions || []).filter((action): action is SafeAction =>
    SAFE_ACTIONS.includes(action as SafeAction),
  );
  return fromApi.length > 0 ? fromApi : fallback;
}

export function resolveMissionAttention(link: CaseAgenticMissionLink | null | undefined): {
  kind: MissionAttentionKind | null;
  title: string;
  detail: string;
  safeActions: SafeAction[];
  missionHref: string | null;
  missionControlHref: string | null;
} {
  if (!link?.mission_id) {
    return {
      kind: null,
      title: "",
      detail: "",
      safeActions: [],
      missionHref: null,
      missionControlHref: null,
    };
  }

  const missionHref =
    link.mission_href ??
    buildMissionDetailWindowUrl(link.mission_id);
  const missionControlHref =
    link.mission_control_href ??
    (link.active_task_id ? missionControlTaskDetailPath(link.active_task_id) : null);

  if (link.chain_failed || link.attention_kind === "chain_failed") {
    return {
      kind: "chain_failed",
      title: "File check handoff failed",
      detail:
        link.attention_summary?.trim() ||
        "Documents were imported but the mission did not advance. Recover the file check to continue.",
      safeActions: safeActionsFromLink(link, ["retry_chain"]),
      missionHref,
      missionControlHref,
    };
  }

  if (link.intake_failed || link.attention_kind === "intake_failed") {
    return {
      kind: "intake_failed",
      title: "File pull incomplete",
      detail:
        link.attention_summary?.trim() ||
        "Some expected CRM files could not be loaded onto the case. Retry the file pull or open the import task for details.",
      safeActions: safeActionsFromLink(link, ["retry_intake"]),
      missionHref,
      missionControlHref:
        missionControlHref ||
        (link.last_failed_runner_task_id
          ? missionControlTaskDetailPath(link.last_failed_runner_task_id)
          : null),
    };
  }

  if (isAgenticMissionBlocked(link)) {
    const taskStatus = (link.agentic_task_status || "").toLowerCase();
    if (taskStatus === "needs_review") {
      return {
        kind: "needs_review",
        title: "Review needed before the file check can continue",
        detail: link.attention_summary?.trim() || link.latest_summary?.trim() || "Additional documents or review may be required.",
        safeActions: safeActionsFromLink(link, ["add_documents"]),
        missionHref,
        missionControlHref,
      };
    }
    if (link.blocked_kind === "client_action") {
      return {
        kind: "blocked_client",
        title: "Waiting on you",
        detail: link.attention_summary?.trim() || link.latest_summary?.trim() || "This case needs your input to continue.",
        safeActions: safeActionsFromLink(link, ["add_documents"]),
        missionHref,
        missionControlHref,
      };
    }
    return {
      kind: "blocked_system",
      title: "File check paused",
      detail:
        link.attention_summary?.trim() ||
        link.latest_summary?.trim() ||
        "Processing stopped unexpectedly. Retry the blocked step or open the mission for more detail.",
      safeActions: safeActionsFromLink(
        link,
        link.active_task_id ? ["retry_task"] : link.can_retry_chain ? ["retry_chain"] : [],
      ),
      missionHref,
      missionControlHref,
    };
  }

  if (link.is_active) {
    return {
      kind: "running",
      title: "File check in progress",
      detail: link.current_stage_display?.trim() || "Marvin is reviewing this case.",
      safeActions: [],
      missionHref,
      missionControlHref,
    };
  }

  return {
    kind: null,
    title: "",
    detail: "",
    safeActions: [],
    missionHref,
    missionControlHref,
  };
}

export function shouldShowMissionAttention(link: CaseAgenticMissionLink | null | undefined): boolean {
  if (!link) return false;
  if (Boolean(link.needs_attention) || link.chain_failed || link.intake_failed) return true;
  return isAgenticMissionBlocked(link);
}
